import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { practitioner, practitioner_category, practitioner_product_category, product_category } from '@prisma/client';
import {
  ColumnDef,
  ColumnFiltersState,
  PaginationState,
  SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { format } from 'date-fns';
import { ArrowDown, ArrowUp, ArrowUpDown, Loader2, MoreHorizontal, Plus } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from 'sonner';
import { useShallow } from 'zustand/react/shallow';
import { trpc } from '~/api/trpc';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/components/ui/alert-dialog';
import { Checkbox } from '~/components/ui/checkbox';
import { Label } from '~/components/ui/label';
import { usePractitionersTableFilteringStore } from '~/stores/practitioners-table-filtering-store';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/components/ui/tabs';
import Content from "../components/layout/Content";

type PractitionerRow = practitioner & {
  practitioner_category: practitioner_category | null;
  practitioner_product_category_practitioner_product_category_practitionerTopractitioner: (practitioner_product_category & {
    product_category: product_category;
  })[];
  performableTreatmentVariants: {
    treatmentVariantId: string;
  }[];
};

function PractitionerActionsCell({ practitioner }: { practitioner: PractitionerRow }) {
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [unarchiveDialogOpen, setUnarchiveDialogOpen] = useState(false);
  const utils = trpc.useUtils();

  const archiveMutation = trpc.practitioners.archive.useMutation({
    onSuccess: () => {
      toast.success('Behandler wurde archiviert.');
      utils.practitioners.getAll.invalidate();
    },
    onError: (error) => {
      toast.error(`Fehler beim Archivieren: ${error.message}`);
    },
  });

  const unarchiveMutation = trpc.practitioners.unarchive.useMutation({
    onSuccess: () => {
      toast.success('Behandler wurde dearchiviert.');
      utils.practitioners.getAll.invalidate();
    },
    onError: (error) => {
      toast.error(`Fehler beim Dearchivieren: ${error.message}`);
    },
  });

  const practitionerName = [practitioner.title, practitioner.firstName, practitioner.lastName]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant='ghost' className='h-8 w-8 p-0'>
            <span className='sr-only'>Open menu</span>
            <MoreHorizontal className='h-4 w-4' />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='end'>
          <DropdownMenuLabel>Actions</DropdownMenuLabel>
          <DropdownMenuItem onClick={() => navigator.clipboard.writeText(String(practitioner.id))}>Copy Practitioner ID</DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem>View Practitioner details</DropdownMenuItem>
          <DropdownMenuItem>Edit Practitioner</DropdownMenuItem>
          <DropdownMenuSeparator />
          {practitioner.archived ? (
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                setUnarchiveDialogOpen(true);
              }}
            >
              Dearchivieren
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                setArchiveDialogOpen(true);
              }}
              className="text-destructive focus:text-destructive"
            >
              Archivieren
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
        <AlertDialogContent onClick={(e) => e.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>Behandler archivieren?</AlertDialogTitle>
            <AlertDialogDescription>
              Sind Sie sicher, dass Sie <strong>{practitionerName}</strong> archivieren möchten?
              Der Behandler wird nicht mehr in der aktiven Liste angezeigt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={archiveMutation.isPending}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              disabled={archiveMutation.isPending}
              onClick={() => archiveMutation.mutate({ id: practitioner.id })}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {archiveMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Archivieren
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={unarchiveDialogOpen} onOpenChange={setUnarchiveDialogOpen}>
        <AlertDialogContent onClick={(e) => e.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>Behandler dearchivieren?</AlertDialogTitle>
            <AlertDialogDescription>
              Sind Sie sicher, dass Sie <strong>{practitionerName}</strong> dearchivieren möchten?
              Der Behandler wird wieder in der aktiven Liste angezeigt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={unarchiveMutation.isPending}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              disabled={unarchiveMutation.isPending}
              onClick={() => unarchiveMutation.mutate({ id: practitioner.id })}
            >
              {unarchiveMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Dearchivieren
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

const practitionerColumns: ColumnDef<PractitionerRow>[] = [
    {
      accessorKey: 'id', // Keep ID for potential actions, maybe hide it later if not needed visually
      header: 'ID',
      // You might want to hide this column visually later using column visibility
      // Or remove it if the ID is never directly displayed.
    },
    {
      id: 'name', // Combine title, first, last name for display
      header: ({ column }) => {
        // Allow sorting by lastName for example
        return (
          <Button
            variant='ghost'
            onClick={() => {
              const currentSort = column.getIsSorted();
              if (currentSort === 'asc') {
                column.toggleSorting(true); // Toggle to desc
              } else if (currentSort === 'desc') {
                column.clearSorting(); // Clear sorting (to none)
              } else {
                column.toggleSorting(false); // Toggle to asc
              }
            }}
          >
            Name
            {column.getIsSorted() === 'asc' ? (
              <ArrowUp className='ml-2 h-4 w-4' />
            ) : column.getIsSorted() === 'desc' ? (
              <ArrowDown className='ml-2 h-4 w-4' />
            ) : (
              <ArrowUpDown className='ml-2 h-4 w-4 opacity-50' />
            )}
          </Button>
        );
      },
      cell: ({ row }) => {
        const { title, firstName, lastName } = row.original;
        const nameParts = [title, firstName, lastName].filter(Boolean); // Filter out empty parts
        return <div className='font-medium'>{nameParts.join(' ')}</div>;
      },
      // Important: Need accessorFn if sorting by a field not directly in accessorKey
      accessorFn: (row) => row.lastName, // Sort by lastName when 'name' column header is clicked
      // Or enable sorting for 'firstName' as well if needed, potentially with another button
      enableSorting: true, // Explicitly enable sorting
      enableHiding: false, // Don't allow hiding this column
    },
    {
      accessorKey: 'practitioner_category',
      header: 'Practitioner Category', // The main category of the practitioner
      cell: ({ row }) => {
        return <div className='max-w-[200px] truncate'>{row.original.practitioner_category?.name ?? 'N/A'}</div>;
      },
      enableSorting: false, // Probably don't need sorting here
    },
    {
      id: 'productCategories',
      header: 'Performable Treatments', // The linked product categories
      cell: ({ row }) => {
        const performableTreatmentVariants = row.original.performableTreatmentVariants;
        const performableTreatmentVariantCount = performableTreatmentVariants.length;

        // Display as badges or comma-separated list
        return (
          <div className='flex flex-wrap gap-1 max-w-[350px]'>
              <Badge variant="secondary" className="whitespace-nowrap">
                {performableTreatmentVariantCount} total
              </Badge>
            {/* Or simpler: return <div>{categoryNames.join(', ')}</div>; */}
          </div>
        );
      },
      enableSorting: false, // Cannot sort by this complex field easily
      enableColumnFilter: false,
    },
    {
      accessorKey: 'active', // Show active status
      header: 'Status',
      cell: ({ row }) => {
        return row.original.archived ? (
          <Badge variant='default' className='bg-green-100 text-green-800 hover:bg-green-100'>Archived</Badge>
        ) : (
          <Badge variant='outline'>Active</Badge>
        );
      },
      enableSorting: false, // Maybe enable later if needed
    },
    {
      id: 'createdAt', // Optional: If you want to show creation date
      header: 'Created At',
      cell: ({ row }) => {
        const date = new Date(row.original.createdAt);
        return <div>{format(date, 'PPP')}</div>;
      },
      accessorFn: (row) => new Date(row.createdAt).getTime(), // For sorting
      enableSorting: true,
    },
    {
      id: 'actions',
      cell: ({ row }) => <PractitionerActionsCell practitioner={row.original} />,
    },
  ];

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  manualPagination?: boolean;
  manualSorting?: boolean;
  manualFiltering?: boolean;
  syncWithUrl?: boolean;
  categoryFilter?: string;
}

// This component specifically handles fetching and rendering Practitioners
const PractitionersDataTable = <TData, TValue>({
  columns,
  manualPagination = true,
  manualSorting = true,
  manualFiltering = true,
  syncWithUrl = true,
  categoryFilter,
}: DataTableProps<TData, TValue>) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const isInitialized = useRef<boolean>(false);
  const navigate = useNavigate();

  // --- Zustand Store Hook for Practitioners ---
  const {
    sorting: storeSorting,
    columnFilters: storeColumnFilters,
    pagination: storePagination,
    setSorting,
    setColumnFilters,
    setPagination,
  } = usePractitionersTableFilteringStore( // Use the PRACTITIONER store
    useShallow((state) => ({
      sorting: state.sorting,
      setSorting: state.setSorting,
      columnFilters: state.columnFilters,
      setColumnFilters: state.setColumnFilters,
      pagination: state.pagination,
      setPagination: state.setPagination,
    }))
  );

  // --- Initial State Synchronization Effect (URL <-> Store) ---
  // This effect is identical to the one in TreatmentsDataTable, just ensure
  // it uses the practitioner store's state and setters.
  useEffect(() => {
    if (!syncWithUrl || isInitialized.current) return;
    isInitialized.current = true;
    console.log("PractitionersDataTable: Initializing state from URL/Store...");


    const newParams = new URLSearchParams(searchParams);
    let needsUrlUpdate = false;

    // --- Sync Sorting ---
    const urlSortField = searchParams.get('sort');
    const urlSortDir = searchParams.get('dir') || 'asc';
    let currentSorting = storeSorting;

    if (urlSortField) {
      const urlSortingState: SortingState = [{ id: urlSortField, desc: urlSortDir === 'desc' }];
      if (JSON.stringify(urlSortingState) !== JSON.stringify(storeSorting)) {
        console.log("Sync: URL -> Store (Sorting)");
        setSorting(urlSortingState);
        currentSorting = urlSortingState;
      }
      if (!searchParams.has('dir') && urlSortField) {
        newParams.set('dir', 'asc');
        needsUrlUpdate = true;
      }
    } else if (storeSorting.length > 0) {
      console.log("Sync: Store -> URL (Sorting)");
      newParams.set('sort', storeSorting[0].id);
      newParams.set('dir', storeSorting[0].desc ? 'desc' : 'asc');
      needsUrlUpdate = true;
    } else {
      if (searchParams.has('sort')) { newParams.delete('sort'); needsUrlUpdate = true; }
      if (searchParams.has('dir')) { newParams.delete('dir'); needsUrlUpdate = true; }
    }

    // --- Sync Filtering ---
    const urlFilters: ColumnFiltersState = [];
    let hasUrlFilters = false;
    for (const [key, value] of searchParams.entries()) {
      if (key.startsWith('filter_')) {
        hasUrlFilters = true;
        urlFilters.push({ id: key.replace('filter_', ''), value });
      }
    }
    let currentFilters = storeColumnFilters;

    if (hasUrlFilters) {
      // Sort for consistent comparison
      const sortedUrlFilters = [...urlFilters].sort((a, b) => a.id.localeCompare(b.id));
      const sortedStoreFilters = [...storeColumnFilters].sort((a, b) => a.id.localeCompare(b.id));
      if (JSON.stringify(sortedUrlFilters) !== JSON.stringify(sortedStoreFilters)) {
        console.log("Sync: URL -> Store (Filtering)");
        setColumnFilters(urlFilters);
        currentFilters = urlFilters;
      }
    } else if (storeColumnFilters.length > 0) {
      console.log("Sync: Store -> URL (Filtering)");
      storeColumnFilters.forEach(filter => {
        newParams.set(`filter_${filter.id}`, String(filter.value));
      });
      needsUrlUpdate = true;
    } else {
      Array.from(searchParams.keys())
        .filter(key => key.startsWith('filter_'))
        .forEach(key => {
          newParams.delete(key);
          needsUrlUpdate = true;
        });
    }

    // --- Sync Pagination ---
    const urlPage = searchParams.get('page');
    const urlSize = searchParams.get('size');
    let currentPagination = storePagination;
    const paginationUpdateFromUrl: Partial<PaginationState> = {};
    let urlHasPagination = false;

    if (urlPage !== null) {
      const pageIndex = Math.max(0, parseInt(urlPage, 10) || 0);
      paginationUpdateFromUrl.pageIndex = pageIndex;
      urlHasPagination = true;
    }
    if (urlSize !== null) {
      const pageSize = Math.max(1, parseInt(urlSize, 10) || storePagination.pageSize);
      paginationUpdateFromUrl.pageSize = pageSize;
      urlHasPagination = true;
    }

    if (urlHasPagination) {
      const newPaginationState = { ...storePagination, ...paginationUpdateFromUrl };
      if (newPaginationState.pageIndex !== storePagination.pageIndex || newPaginationState.pageSize !== storePagination.pageSize) {
        console.log("Sync: URL -> Store (Pagination)");
        setPagination(newPaginationState);
        currentPagination = newPaginationState;
      }
      if (String(currentPagination.pageIndex) !== urlPage) {
        newParams.set('page', String(currentPagination.pageIndex));
        needsUrlUpdate = true;
      }
      if (String(currentPagination.pageSize) !== urlSize) {
        newParams.set('size', String(currentPagination.pageSize));
        needsUrlUpdate = true;
      }
    } else {
      console.log("Sync: Store -> URL (Pagination)");
      newParams.set('page', String(storePagination.pageIndex));
      newParams.set('size', String(storePagination.pageSize));
      needsUrlUpdate = true;
    }

    // --- Apply URL Updates ---
    if (needsUrlUpdate) {
      console.log("Applying URL updates:", newParams.toString());
      setSearchParams(newParams, { replace: true });
    }
    console.log("PractitionersDataTable: Initialization complete.");
  }, [syncWithUrl, searchParams, setSearchParams]);


  // --- Derive API Query Params from the URL ---
  const queryPage = parseInt(searchParams.get('page') || String(storePagination.pageIndex), 10);
  const queryPageSize = parseInt(searchParams.get('size') || String(storePagination.pageSize), 10);
  const queryFilters: Record<string, string | undefined> = {};

  for (const [key, value] of searchParams.entries()) {
    if (key.startsWith('filter_')) {
      queryFilters[key.replace('filter_', '')] = value;
    }
  }

  if (categoryFilter) {
    queryFilters['categoryId'] = categoryFilter;
  }

  const [showArchived, setShowArchived] = useState<boolean>(false);

  const {
    data: practitionersResponse,
    isLoading: isQueryLoading,
    isFetching: isQueryFetching,
  } = trpc.practitioners.getAll.useQuery(
    {
      page: queryPage,
      pageSize: queryPageSize,
      filters: queryFilters,
      showArchived: showArchived,
    },
    {
      suspense: true,
    }
  );

  const isLoading = isQueryLoading;
  const isFetching = isQueryFetching;

  const createPractitionerMutation = trpc.practitioners.create.useMutation();

  // Sync sorting state with URL
  useEffect(() => {
    if (!syncWithUrl || !isInitialized.current) return;
    const currentSortField = searchParams.get('sort');
    const currentSortDir = searchParams.get('dir');
    const storeSortField = storeSorting[0]?.id;
    const storeSortDir = storeSorting[0]?.desc ? 'desc' : 'asc';

    if (storeSortField !== currentSortField || (storeSortField && storeSortDir !== currentSortDir)) {
      console.log("Effect: Store -> URL (Sorting)");
      const newParams = new URLSearchParams(searchParams);
      if (storeSorting.length > 0) {
        newParams.set('sort', storeSorting[0].id);
        newParams.set('dir', storeSorting[0].desc ? 'desc' : 'asc');
      } else {
        newParams.delete('sort');
        newParams.delete('dir');
      }
      setSearchParams(newParams, { replace: true });
    }
  }, [storeSorting, syncWithUrl, searchParams, setSearchParams]);

  // Sync filter state with URL
  useEffect(() => {
    if (!syncWithUrl || !isInitialized.current) return;
    const newParams = new URLSearchParams(searchParams);
    let filtersChanged = false;
    const urlFiltersMap = new Map<string, string>();
    Array.from(newParams.keys())
      .filter((key) => key.startsWith('filter_'))
      .forEach((key) => urlFiltersMap.set(key.replace('filter_', ''), newParams.get(key)!));
    const storeFiltersMap = new Map<string, string>();
    storeColumnFilters.forEach(f => storeFiltersMap.set(f.id, String(f.value)));

    urlFiltersMap.forEach((_, id) => {
      if (!storeFiltersMap.has(id)) {
        newParams.delete(`filter_${id}`);
        filtersChanged = true;
      }
    });
    storeFiltersMap.forEach((value, id) => {
      if (urlFiltersMap.get(id) !== value) {
        newParams.set(`filter_${id}`, value);
        filtersChanged = true;
      }
    });

    if (filtersChanged) {
      console.log("Effect: Store -> URL (Filtering)");
      setSearchParams(newParams, { replace: true });
    }
  }, [storeColumnFilters, syncWithUrl, searchParams, setSearchParams]);


  // Sync pagination state with URL
  useEffect(() => {
    if (!syncWithUrl || !isInitialized.current) return;
    const currentPage = searchParams.get('page');
    const currentSize = searchParams.get('size');
    const storePage = String(storePagination.pageIndex);
    const storeSize = String(storePagination.pageSize);

    if (currentPage !== storePage || currentSize !== storeSize) {
      console.log("Effect: Store -> URL (Pagination)");
      const newParams = new URLSearchParams(searchParams);
      newParams.set('page', storePage);
      newParams.set('size', storeSize);
      setSearchParams(newParams, { replace: true });
    }
  }, [storePagination, syncWithUrl, searchParams, setSearchParams]);

  // --- React Table Instance ---
  const [rowSelection, setRowSelection] = useState({}); // Local UI state if needed

  const table = useReactTable({
    data: practitionersResponse?.data ?? [], // Use practitioner data
    columns: columns, // Use practitioner columns
    pageCount: practitionersResponse?.pageCount ?? -1,
    state: {
      // Feed state from the PRACTITIONER store
      sorting: storeSorting,
      columnFilters: storeColumnFilters,
      pagination: storePagination,
      rowSelection,
    },
    // Use setters from the PRACTITIONER store
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onRowSelectionChange: setRowSelection,
    // Manual flags
    manualPagination,
    manualSorting,
    manualFiltering,
    // Core model functions
    getCoreRowModel: getCoreRowModel(),
    // Client-side fallbacks (optional but included in your original code)
    getPaginationRowModel: manualPagination ? undefined : getPaginationRowModel(),
    getSortedRowModel: manualSorting ? undefined : getSortedRowModel(),
    getFilteredRowModel: manualFiltering ? undefined : getFilteredRowModel(),
    // debugTable: true,
  });

  // --- Clamp Page Index Effect (Identical logic) ---
  useEffect(() => {
    if (!practitionersResponse || isLoading || isFetching || !manualPagination) return;
    const currentPageIndex = table.getState().pagination.pageIndex;
    const pageCount = practitionersResponse.pageCount;
    if (pageCount > 0 && currentPageIndex >= pageCount) {
      console.log(`Clamping page index from ${currentPageIndex} to ${pageCount - 1}`);
      table.setPageIndex(Math.max(0, pageCount - 1));
    }
  }, [practitionersResponse, table, isLoading, isFetching, manualPagination]);

  // --- Render Logic ---

  // Loading Skeleton (similar structure)
  if (isLoading && !isInitialized.current) { // Show skeleton only on initial mount load
    return (
      <div className='bg-white rounded-lg shadow-sm p-6'>
        <div className='flex items-center justify-between py-4'>
          <Skeleton className='h-10 w-[250px]' />
          <Skeleton className='h-10 w-[150px]' />
        </div>
        <div className='rounded-md border border-gray-200'>
          {/* Skeleton table */}
          <Table>
            <TableHeader>
              <TableRow>
                {/* Use column count from practitionerColumns */}
                {Array.from({ length: columns.length }).map((_, index) => (
                  <TableHead key={index}>
                    <Skeleton className='h-6 w-full' />
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array.from({ length: table.getState().pagination.pageSize }).map((_, rowIndex) => (
                <TableRow key={rowIndex}>
                  {Array.from({ length: columns.length }).map((_, colIndex) => (
                    <TableCell key={colIndex}>
                      <Skeleton className='h-6 w-full' />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className='flex items-center justify-between space-x-2 py-4 mt-2'>
          <Skeleton className='h-6 w-24' />
          <div className="flex space-x-2">
            <Skeleton className='h-9 w-20' />
            <Skeleton className='h-9 w-20' />
          </div>
        </div>
      </div>
    );
  }

  // Main Table Render
  return (
    <div className='bg-white rounded-lg shadow-sm p-6'>
      {/* Filter Input & Add Button */}
      <div className='flex items-center justify-between py-4'>
        <div className='flex flex-col gap-2'>
          <div className='flex gap-4 items-center'>
            <Input
              placeholder='Search for practitioner...'
              value={(table.getColumn('name')?.getFilterValue() as string) ?? ''}
              onChange={(event) => {
                table.getColumn('name')?.setFilterValue(event.target.value);
              }}
              className='max-w-sm focus:ring-primary'
            />
            {isFetching && (
              <div className='flex items-center text-sm text-muted-foreground'>
                <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                Loading...
              </div>
            )}
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox 
              id="show-archived" 
              checked={showArchived} 
              onCheckedChange={(checked) => setShowArchived(checked === 'indeterminate' ? false : checked)} 
            />
            <Label 
              htmlFor="show-archived" 
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Show archived practitioners
            </Label>
          </div>
        </div>
        <Button
          variant='outline'
          className='border-primary-400 text-primary-400 hover:text-primary-400 hover:bg-primary-400/10 transition-all'
          disabled={createPractitionerMutation.isPending}
          onClick={async () => {
            try {
              const practitioner = await createPractitionerMutation.mutateAsync();
              if(practitioner.id) {
                navigate(`/practitioner/${practitioner.id}`)
              }
            } catch (error) {
              const message = error instanceof Error ? error.message : 'Unknown error';
              toast.error(`Practitioner konnte nicht erstellt werden: ${message}`);
            }
          }}
        >
          {createPractitionerMutation.isPending ? <Loader2 className='animate-spin' /> : <Plus className='mr-2 h-4 w-4' />}
          Create Practitioner
        </Button>
      </div>

      {/* Table */}
      <div className='rounded-md border border-gray-200 overflow-hidden'>
        <Table>
          <TableHeader className='bg-gray-50'>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id} className='font-medium text-gray-700'>
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  className='cursor-pointer hover:bg-gray-50 transition-colors relative'
                >
                  {row.getVisibleCells().map((cell, index) => (
                    <TableCell key={cell.id} style={{ width: cell.column.getSize() !== 150 ? cell.column.getSize() : undefined }}>
                      {index === 0 && (
                        <Link
                          to={`/practitioner/${row.original.id}`}
                          className="absolute inset-0 z-[1]"
                          tabIndex={-1}
                        />
                      )}
                      {cell.column.id === 'actions' ? (
                        <div className="relative z-[2]">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </div>
                      ) : (
                        flexRender(cell.column.columnDef.cell, cell.getContext())
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className='h-24 text-center'>
                  {isFetching ? (
                    <div className='flex justify-center items-center'>
                      <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                      Loading data...
                    </div>
                  ) : (
                    // More specific "no results" message
                    storeColumnFilters.length > 0 // Check if filters are active
                      ? 'No practitioners found matching your filters.'
                      : 'No practitioners found.'
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination Controls (Identical structure) */}
      <div className='flex items-center justify-between space-x-2 py-4 mt-2'>
        <div className='flex items-center space-x-2'>
          <select
            value={table.getState().pagination.pageSize}
            onChange={e => {
              table.setPageSize(Number(e.target.value))
            }}
            className="border border-gray-300 rounded px-2 py-1 text-sm"
            disabled={isFetching}
          >
            {[10, 20, 30, 40, 50].map(pageSize => (
              <option key={pageSize} value={pageSize}>
                Show {pageSize}
              </option>
            ))}
          </select>
          <div className='text-sm text-muted-foreground'>
            Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount() > 0 ? table.getPageCount() : 1}
          </div>
          <Button
            variant='outline'
            size='sm'
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage() || isFetching}
            className='border-gray-300 hover:bg-gray-50'
          >
            Previous
          </Button>
          <Button
            variant='outline'
            size='sm'
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage() || isFetching}
            className='border-gray-300 hover:bg-gray-50'
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};


























const Practitioners = () => {
  const { data: categories } = trpc.practitionerCategories.getAllSimple.useQuery();
  const [activeTab, setActiveTab] = useState<string>("");

  const defaultTab = categories?.[0]?.id ? String(categories[0].id) : "uncategorized";
  const currentTab = activeTab || defaultTab;

  return (
    <Content>
      <div className='container mx-auto py-10'>
        <h1 className='text-2xl font-bold mb-6'>Behandler</h1>
        <Tabs value={currentTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            {categories?.map(cat => (
              <TabsTrigger key={cat.id} value={String(cat.id)}>
                {cat.name}
              </TabsTrigger>
            ))}
            <TabsTrigger value="uncategorized">Ohne Kategorie</TabsTrigger>
          </TabsList>
          {categories?.map(cat => (
            <TabsContent key={cat.id} value={String(cat.id)}>
              <PractitionersDataTable
                columns={practitionerColumns}
                categoryFilter={String(cat.id)}
                manualPagination={true}
                manualSorting={true}
                manualFiltering={true}
                syncWithUrl={true}
              />
            </TabsContent>
          ))}
          <TabsContent value="uncategorized">
            <PractitionersDataTable
              columns={practitionerColumns}
              categoryFilter="null"
              manualPagination={true}
              manualSorting={true}
              manualFiltering={true}
              syncWithUrl={true}
            />
          </TabsContent>
        </Tabs>
      </div>
    </Content>
  );
};

export default Practitioners;
